import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { Marathon } from '@/api/courses';
import type { Enrollment } from '@/api/users';
import { enrollmentTransferApi, type TransferReport } from '@/api/enrollmentTransfer';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { apiErrorCode } from '@/lib/apiErrorCode';
import {
  canRunTransfer,
  doneTitles,
  errorProblems,
  errorReport,
  transferCodeKey,
  transferErrorKey,
  transferSources,
  transferTableLabel,
  transferTargets,
} from '@/lib/enrollmentTransfer';

/**
 * «Перенести на другой курс»: выбор курса того же подтипа → пробный прогон (таблица, что переедет, и строки без пары)
 * → «Перенести». Договор — `docs/CONTRACTS/enrollment-transfer.md` §9.
 *
 * ⚠️ НАСТОЯЩИЙ ПЕРЕНОС — ТОЛЬКО ПО ОТПЕЧАТКУ ПРОБНОГО (`expect`). Сделал ученик что-то между прогоном и нажатием —
 * сервер отвечает `409 rows_changed` с новым отчётом; таблица подменяется, и админ смотрит на свежие числа.
 */
export function TransferDialog({
  open,
  onClose,
  studentId,
  marathons,
  enrollments,
}: {
  open: boolean;
  onClose: () => void;
  studentId: string;
  marathons: Marathon[];
  enrollments: Enrollment[];
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const ask = useConfirm();
  const qc = useQueryClient();
  const sources = transferSources(marathons, enrollments);
  const [from, setFrom] = useState(sources[0]?.id ?? '');
  const [to, setTo] = useState('');
  const [report, setReport] = useState<TransferReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<TransferReport | null>(null);
  const targets = transferTargets(marathons, enrollments, from);

  const reset = () => {
    setReport(null);
    setError(null);
  };

  const preview = useMutation({
    mutationFn: () => enrollmentTransferApi.preview(studentId, from, to),
    onMutate: reset,
    onSuccess: (r) => setReport(r),
    onError: (e) => {
      setError(e);
      setReport(errorReport(e));
    },
  });

  const run = useMutation({
    mutationFn: (expect: string) => enrollmentTransferApi.run(studentId, from, to, expect),
    onSuccess: (r) => {
      setDone(r);
      setError(null);
      qc.invalidateQueries({ queryKey: ['admin', 'student', studentId] });
      qc.invalidateQueries({ queryKey: ['admin', 'transfers', studentId] });
      // Баллы по курсам переехали вместе с уроками — карточка баллов иначе показывала бы старый курс (стенд 28.09).
      qc.invalidateQueries({ queryKey: ['progress', 'student-points', studentId] });
    },
    onError: (e) => {
      setError(e);
      // rows_changed несёт свежий отчёт — показываем его вместо устаревшего.
      const fresh = errorReport(e);
      if (fresh) setReport(fresh);
    },
  });

  const problems = report?.problems?.length ? report.problems : errorProblems(error);
  const code = error ? apiErrorCode(error) : undefined;
  const toTitle = targets.find((x) => x.id === to)?.title ?? '';
  const fromTitle = sources.find((x) => x.id === from)?.title ?? '';

  // Кнопки: на телефоне — внизу окна во весь экран (правило 3), на компьютере — под отчётом, как было.
  const buttons = done ? (
    <div className={isMobile ? 'grid' : 'flex justify-end'}>
      <Button onClick={onClose}>{t('adminTransfer.close')}</Button>
    </div>
  ) : (
    <div className={isMobile ? 'grid grid-cols-3 gap-2' : 'flex flex-wrap justify-end gap-2 pt-1'}>
      <Button variant="ghost" onClick={onClose} disabled={run.isPending}>
        {t('common.cancel')}
      </Button>
      <Button variant="secondary" onClick={() => preview.mutate()} loading={preview.isPending} disabled={!from || !to || run.isPending}>
        {t('adminTransfer.check')}
      </Button>
      <Button
        variant="danger"
        loading={run.isPending}
        disabled={!canRunTransfer(report) || (error != null && code !== 'rows_changed') || preview.isPending}
        onClick={async () => {
          if (!report?.expect) return;
          if (await ask({ message: t('adminTransfer.confirm', { from: fromTitle, to: toTitle }), danger: true })) {
            run.mutate(report.expect);
          }
        }}
      >
        {t('adminTransfer.run')}
      </Button>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={t('adminTransfer.title')} size="lg" phoneFull footer={isMobile ? buttons : undefined}>
      {done ? (
        <div className="space-y-3 text-sm">
          <p className="text-success">{t('adminTransfer.done', doneTitles(done, fromTitle, toTitle))}</p>
          {done.verify && (
            <p className={done.verify.ok ? 'text-success' : 'text-error'}>
              {done.verify.ok
                ? t('adminTransfer.verifyOk', { n: done.verify.groups.length })
                : t('adminTransfer.verifyBad', {
                    n: done.verify.groups.filter((g) => g.ok !== g.rows).length,
                    total: done.verify.groups.length,
                  })}
            </p>
          )}
          {done.refresh_ok === false && <p className="text-on-surface-variant">{t('adminTransfer.refreshLater')}</p>}
          <p className="text-on-surface-variant">{t('adminTransfer.doneHint')}</p>
          {!isMobile && buttons}
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <p className="text-on-surface-variant">{t('adminTransfer.intro')}</p>
          {sources.length === 0 ? (
            <p className="text-warning">{t('adminTransfer.noSources')}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                {t('adminTransfer.from')}
                <select
                  value={from}
                  onChange={(e) => { setFrom(e.target.value); setTo(''); reset(); }}
                  className="mt-1 w-full rounded-lg border border-outline bg-surface px-3 py-2"
                >
                  {sources.map((m) => (
                    <option key={m.id} value={m.id}>{m.title}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                {t('adminTransfer.to')}
                <select
                  value={to}
                  onChange={(e) => { setTo(e.target.value); reset(); }}
                  className="mt-1 w-full rounded-lg border border-outline bg-surface px-3 py-2"
                >
                  <option value="">{t('adminTransfer.toPick')}</option>
                  {targets.map((m) => (
                    <option key={m.id} value={m.id} disabled={!m.hasEnrollment}>
                      {m.hasEnrollment ? m.title : t('adminTransfer.toNoEnrollment', { title: m.title })}
                    </option>
                  ))}
                </select>
                {targets.length === 0 && <span className="mt-1 block text-xs text-warning">{t('adminTransfer.noTargets')}</span>}
                {targets.some((m) => !m.hasEnrollment) && (
                  <span className="mt-1 block text-xs text-on-surface-variant">{t('adminTransfer.grantFirst')}</span>
                )}
              </label>
            </div>
          )}

          {report && (
            <div className="overflow-x-auto">
              {/* Четыре узких столбца влезают и в 390: запас ширины 520 — только от md, на телефоне без прокрутки вбок. */}
              <table className="w-full text-left md:min-w-[520px]">
                <thead className="text-xs uppercase tracking-label text-on-surface-variant">
                  <tr>
                    <th className="py-1.5 pr-3">{t('adminTransfer.colWhat')}</th>
                    <th className="py-1.5 pr-3 text-right">{t('adminTransfer.colRows')}</th>
                    <th className="py-1.5 pr-3 text-right">{t('adminTransfer.colPaired')}</th>
                    <th className="py-1.5 pr-3 text-right">{t('adminTransfer.colInTarget')}</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {report.tables.map((row) => {
                    const bad = row.paired < row.rows || row.in_target > 0;
                    return (
                      <tr key={row.table} className={`border-t border-outline-variant/40 ${bad ? 'text-error' : ''}`}>
                        <td className="py-1.5 pr-3">{transferTableLabel(row.table, t)}</td>
                        <td className="py-1.5 pr-3 text-right">{row.rows}</td>
                        <td className="py-1.5 pr-3 text-right">{row.paired}</td>
                        <td className="py-1.5 pr-3 text-right">{row.in_target}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {report && report.unpaired?.length > 0 && (
            <div className="rounded-card bg-surface-container-highest p-3">
              <p className="font-medium">{t('adminTransfer.unpairedTitle', { count: report.unpaired.length })}</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                {report.unpaired.map((u) => (
                  <li key={`${u.table}-${u.row_key}`}>
                    {transferTableLabel(u.table, t)} · {t(`adminTransfer.kind_${u.kind}`)} ·{' '}
                    {u.title ?? u.block_type ?? (u.node_id ?? '').slice(0, 8)}
                    {!u.has_mirror_key && ` · ${t('adminTransfer.noMirrorKey')}`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error != null && (
            <div className="rounded-card bg-error/5 p-3 text-error">
              <p>{t(transferErrorKey(error))}</p>
              {problems.filter((p) => p !== code).map((p) => (
                <p key={p} className="mt-1 text-xs">{t(transferCodeKey(p))}</p>
              ))}
            </div>
          )}

          {report && canRunTransfer(report) && !error && (
            <p className="text-success">{t('adminTransfer.readyHint', { from: fromTitle, to: toTitle })}</p>
          )}

          {!isMobile && buttons}
        </div>
      )}
    </Modal>
  );
}
